import type { EditorBlock } from '@/store/editorSlice';
import { SELECTOR_FALLBACK_BLOCK_TYPES } from './selectorFallbackBlockTypes';
import { resolvePlanSelectorDefaultPriceId } from '@/player/planSelectorPlans';
import { resolveMainPayBlock } from './resolveMainPayBlock';

/**
 * Resolve the Stripe price id a pay button should charge: prefer the button's
 * own `stripePriceId`, otherwise fall back to the first price-bearing block on
 * the same screen (e.g. a `price_block` the user configured). This makes a
 * paywall work even when the price is attached to the price block, not the
 * button.
 *
 * WEB-1113 (аудит 22.08): `allBlocks` is expected to already be the CALLER's
 * visible-only block list (PaywallScreenWizard's `visibleBlocks`, not the raw
 * `currentScreen.blocks`) — a block hidden by a visibility rule must never be
 * a source of the charged price. `hiddenBlockIds` is a second, independent
 * safety net inside this function itself: it also strips a hidden block's
 * STORED selection from being trusted even when it is re-discovered through
 * the `funnelBlocks` cross-screen fallback below (which stays unfiltered —
 * see the comment on that line).
 */
/**
 * WEB-1302 (хвост): ЕДИНАЯ лестница поиска живого выбора «Выбора тарифа» —
 * сначала блоки текущего экрана, затем блоки всей воронки. Ею пользуются и
 * кнопка оплаты (resolvePriceId ниже), и юридический текст «Условия оплаты»
 * (PublicBlockRendererExtended, payment_terms): текст обязан обещать ту же
 * цену, что спишет кнопка. Правило нарочно живёт в одном месте — вторая
 * копия лестницы уже разъезжалась однажды (история WEB-1302).
 *
 * WEB-447 §4.3a (MONEY): a plan_selector's live selection is the user's
 * explicit choice and must drive checkout above any static fallback. The
 * selector mirrors its selection — and its default, on mount — into form
 * values keyed by its block id, so reading it here charges the picked plan.
 * WEB-703 п.3 (ратиф. PM 2026-07-17): the selection acts CROSS-SCREEN — form
 * values live for the whole session, so a selector on an earlier screen must
 * drive a pay button on a later one. Same-screen selector wins when both exist.
 *
 * WEB-1113: the `funnelBlocks?.find(...)` fallback below is intentionally
 * LEFT UNCHANGED (не менять — решение оркестратора 22.08) — it has no
 * access to another screen's hiddenSet, so a plan_selector hidden by ITS
 * OWN screen's rules can still be matched through it and stay trusted. That
 * is a known, accepted gap for a genuinely cross-screen selector; open
 * question for the product owner, not fixed here. What IS fixed: a
 * plan_selector belonging to the CURRENT screen (whose hiddenBlockIds this
 * function was given) never drives price once hidden, regardless of
 * whether `allBlocks.find` or the `funnelBlocks` fallback matched it.
 * WEB-1302 (хвост, п.5): payment_terms наследует эту же оговорку ОСОЗНАННО —
 * расхождение текста и списания хуже, чем общий для обоих пробел. Не
 * «чинить» одну половину отдельно.
 *
 * РЕШЕНИЕ ВЛАДЕЛЬЦА Р2 (24.08, Linear WEB-1302) — расхождение, записанное
 * ревизией фикс-волны 23.08, ЗАКРЫТО. Вопрос стоял так: живого выбора
 * тарифа нет, у кнопки задана своя цена, а рядом на экране лежит блок с
 * другой ценой — чья цена правда? Ответ владельца: **правда — цена
 * кнопки**, юридический текст обязан обещать то, что реально спишется.
 * Поэтому в лестницу юр-текста добавлена ВТОРАЯ ступень «своя цена
 * платёжной кнопки этого экрана» (resolveScreenPayButtonPriceId ниже) —
 * зеркало ступени `block.stripePriceId` у кнопки, которой у блока условий
 * своего поля нет (его ручное поле paymentTermsPriceId проверяется ПОСЛЕ
 * лестницы, последним запасным вариантом). Обе лестницы теперь описаны
 * одним набором ступеней в этом файле; парный комментарий — у payment_terms
 * в PublicBlockRendererExtended.tsx.
 */
/**
 * ПЕРВЫЙ «Выбор тарифа»: сначала текущий экран, затем вся воронка
 * (кросс-экранно), и только если он не скрыт правилом видимости своего
 * экрана. С WEB-3335 по нему берутся только умолчание (покупатель не видел ни
 * одного селектора) и подпись ступени в панели; живой выбор ищется
 * findLastChosenSelection ниже — последний выбор в любом показанном.
 *
 * Вынесено отдельно РАДИ ОДНОЙ КОПИИ правила: этим же поиском пользуется
 * описатель ступеней для панели редактора (describePaymentTermsPriceStep
 * ниже). Если бы описатель искал селектор сам, он рано или поздно разошёлся
 * бы с резолвером и стал бы врать автору — ровно тот класс, из-за которого
 * лестницы текста и кнопки уже разъезжались однажды (история WEB-1302).
 */
function findPlanSelector(
  allBlocks: EditorBlock[],
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): EditorBlock | undefined {
  const planSelector =
    allBlocks.find((b) => b.type === 'plan_selector') ??
    funnelBlocks?.find((b) => b.type === 'plan_selector');
  if (!planSelector || hiddenBlockIds?.has(planSelector.id)) return undefined;
  return planSelector;
}

/**
 * WEB-3335 (деньги) — критерий владельца 15.10 ~19:40: «на экране оплаты без
 * своего селектора списывается тариф, который покупатель последним выбрал в
 * любом показанном ему селекторе; не видел ни одного — прежнее правило 06.10
 * (умолчание первого селектора воронки)».
 *
 * Дефект до починки: живой выбор читался только у ПЕРВОГО селектора воронки
 * (findPlanSelector), а без его значения бралось его умолчание — выбор
 * посетителя во втором селекторе на другом экране не читался вовсе, со счёта
 * уходил чужой тариф.
 *
 * «Показан» — у селектора есть непустое значение в форме: селектор пишет туда
 * свой выбор, в том числе умолчание при показе (PlanSelectorBlock).
 * «Последним» — позже других в порядке ключей `values`: провайдер значений
 * ставит каждую ЯВНУЮ запись в конец (`writeLast` в FormValuesContext.tsx), а
 * умолчание при показе — перед всеми явными и только если значения ещё нет
 * (`writeDefault`, доработка по решению владельца 15.10, ночь: «тариф по
 * умолчанию не должен перебивать выбор, который покупатель сделал сам»).
 * Поэтому последний ключ — последний явный выбор, а пока покупатель ничего
 * не выбирал сам — последнее показанное умолчание. Скрытый правилом селектор текущего
 * экрана не в счёт (WEB-1113); скрытость селекторов ЧУЖИХ экранов здесь не
 * видна — прежний известный пробел, не расширяется и не сужается.
 */
function findLastChosenSelection(
  funnelBlocks: EditorBlock[] | undefined,
  values: Record<string, string>,
  hiddenBlockIds?: Set<string>,
): string | undefined {
  const selectors = new Set(
    funnelBlocks
      ?.filter((b) => b.type === 'plan_selector' && !hiddenBlockIds?.has(b.id))
      .map((b) => b.id),
  );
  // Пустое значение — не выбор; последний подходящий ключ — последний выбор.
  return Object.keys(values)
    .filter((key) => selectors.has(key) && values[key])
    .map((key) => values[key])
    .pop();
}

export function resolveSelectedPlanPriceId(
  allBlocks: EditorBlock[],
  values: Record<string, string>,
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  // Свой селектор экрана главнее чужих (WEB-703 п.3); скрытый правилом —
  // цену не задаёт вовсе, чужие тоже не подставляются (WEB-1113).
  const screenSelector = allBlocks.find((b) => b.type === 'plan_selector');
  if (screenSelector) {
    if (hiddenBlockIds?.has(screenSelector.id)) return undefined;
    return values[screenSelector.id] || undefined;
  }
  // Экран без своего селектора — последний выбор в показанном (WEB-3335).
  return findLastChosenSelection(funnelBlocks, values, hiddenBlockIds);
}

/**
 * WEB-1714 — решение владельца 06.10 (реестр 2368-Р33), пункт 1: «живого
 * выбора тарифа нет → берётся цена по умолчанию „Выбора тарифа“ с любого
 * экрана воронки (`planSelectorDefaultPriceId`, иначе первый тариф)».
 *
 * Дыра, которую закрывает ступень. Умолчание селектора попадает в значения
 * формы только при ПОКАЗЕ блока (PlanSelectorBlock, эффект на монтировании).
 * Посетитель, которого ветвление или порядок экранов привёл к оплате в обход
 * экрана с селектором, живого выбора не имеет; своя цена кнопки при селекторе
 * в воронке заперта (решение 25.08) — и цена была `undefined`, оплата не
 * начиналась. А у воронок, где цену кнопке сохранили до запирания, при обходе
 * списывалась она, хотя плашка в конструкторе обещает «спишется цена тарифа».
 *
 * Селектор умолчания — findPlanSelector (текущий экран, затем первый в
 * воронке, скрытый правилом селектор текущего экрана не в счёт). WEB-3335:
 * умолчание берётся, только когда ни один селектор покупателю не показан —
 * иначе выше сработал последний выбор (findLastChosenSelection). Какой тариф «по умолчанию» — одно
 * правило с рендерером (`resolvePlanSelectorDefaultPriceId`), поэтому при
 * обходе спишется ровно тот тариф, который посетитель увидел бы выбранным.
 *
 * Ступень стоит сразу после живого выбора и РАНЬШЕ своей цены кнопки: ради
 * этого порядка поле цены кнопки и заперто. На экране с самим селектором
 * ничего не меняется: там умолчание уже лежит в значениях формы с показа.
 */
export function resolvePlanSelectorPriceId(
  allBlocks: EditorBlock[],
  values: Record<string, string>,
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  const live = resolveSelectedPlanPriceId(allBlocks, values, funnelBlocks, hiddenBlockIds);
  if (live) return live;
  const planSelector = findPlanSelector(allBlocks, funnelBlocks, hiddenBlockIds);
  return planSelector ? resolvePlanSelectorDefaultPriceId(planSelector) : undefined;
}

/** Есть ли у блока непустая собственная цена и не скрыт ли он правилом. */
function usablePriceOf(
  block: EditorBlock,
  hiddenBlockIds?: Set<string>,
): string | undefined {
  if (hiddenBlockIds?.has(block.id)) return undefined;
  return typeof block.stripePriceId === 'string' && block.stripePriceId.length > 0
    ? block.stripePriceId
    : undefined;
}

/**
 * Ступень «первый ВИДИМЫЙ блок с ценой на этом экране» — общая для кнопки
 * оплаты и для юридического текста, чтобы третья ступень обеих лестниц не
 * разъехалась (история WEB-1302: одно правило в двух местах).
 *
 * Static fallback stays same-screen on purpose: silently charging a price
 * block the visitor never saw on this screen would be a money surprise.
 * WEB-1113: also excludes a hidden block explicitly — belt-and-braces on top
 * of callers already passing a visible-only `allBlocks`.
 */
export function resolveFirstPricedBlockId(
  allBlocks: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  for (const b of allBlocks) {
    const price = usablePriceOf(b, hiddenBlockIds);
    if (price) return price;
  }
  return undefined;
}

/**
 * Ступень «своя цена платёжной кнопки этого экрана» (решение владельца Р2,
 * 24.08).
 *
 * WEB-1854 (решение владельца 03.09, вариант А) — ЧЬЯ ИМЕННО КНОПКА. Раньше
 * ступень брала ПЕРВУЮ платёжную кнопку с годной ценой, а липкая полоса
 * покупки — ПОСЛЕДНЮЮ (или помеченную «Всегда показывать»). На экране без
 * «Выбора тарифа» с двумя кнопками разной цены текст «Цены по частям»
 * описывал верхнюю, а полоса печатала и списывала нижнюю: покупатель читал
 * одну сумму, платил другую. Теперь «какая кнопка главная» решает ОДНА
 * лестница на обе поверхности — `resolveMainPayBlock` (явное «Всегда
 * показывать» → первый такой блок, иначе последний платёжный блок), своей
 * копии правила тут нет. Побочное следствие принято владельцем: у части
 * опубликованных воронок текст сменит сумму.
 *
 * ГЛАВНАЯ КНОПКА БЕЗ СВОЕЙ ЦЕНЫ — ступень пуста, лестница идёт дальше, на
 * «первый видимый блок с ценой». Это ровно то, что в этом случае спишет сама
 * кнопка (`resolvePriceId`: своя цена → первый видимый блок с ценой), поэтому
 * текст и списание совпадают и здесь. Прежняя редакция вместо этого искала
 * цену у ДРУГОЙ платёжной кнопки экрана — и снова обещала не то, что спишет
 * главная.
 *
 * ЗАПАСНАЯ СТУПЕНЬ (для экрана БЕЗ главной кнопки, не изменена WEB-1854). На
 * экране может вообще не быть блока, способного вести полосу, — например,
 * только экспресс-кошелёк. Тогда, как и до этой правки, берётся первый
 * видимый блок ИЗ ПЛАТЁЖНЫХ ТИПОВ со своей ценой:
 * SELECTOR_FALLBACK_BLOCK_TYPES — тот же единственный перечень, которым гейт
 * публикации отличает блоки, реально списывающие деньги своим `stripePriceId`
 * (button_to_pay, stripe_wallets_express, stripe_checkout_form). Четвёртая
 * копия списка не заводится: за синхронность перечня с сервером уже отвечает
 * сторож pricing-gate-fe-mirror.web1302.spec.ts. Кошельки специально
 * оставлены в запасной ступени, но НЕ в лестнице главной кнопки — вести
 * полосу они не могут, и назначить их главными означало бы сменить владельца
 * полосы у живых воронок (за границей решения от 03.09).
 */
export function resolveScreenPayButtonPriceId(
  screenBlocks: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  const mainPayBlock = resolveMainPayBlock(screenBlocks, hiddenBlockIds);
  if (mainPayBlock) return usablePriceOf(mainPayBlock, hiddenBlockIds);

  for (const b of screenBlocks) {
    if (!SELECTOR_FALLBACK_BLOCK_TYPES.includes(b.type)) continue;
    const price = usablePriceOf(b, hiddenBlockIds);
    if (price) return price;
  }
  return undefined;
}

export function resolvePriceId(
  block: EditorBlock,
  allBlocks: EditorBlock[],
  values: Record<string, string> = {},
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  // Живой выбор тарифа, а без него — тариф селектора по умолчанию (WEB-1714):
  // общей лестницей с юридическим текстом (см. resolvePlanSelectorPriceId).
  const selected = resolvePlanSelectorPriceId(
    allBlocks,
    values,
    funnelBlocks,
    hiddenBlockIds,
  );
  if (selected) return selected;
  if (block.stripePriceId) return block.stripePriceId;
  return resolveFirstPricedBlockId(allBlocks, hiddenBlockIds);
}

/**
 * Лестница цены для юридического текста «Условия оплаты» (payment_terms).
 *
 * РАТИФИЦИРОВАНО владельцем (Р2, 24.08, WEB-1302) и полностью совпадает с
 * лестницей кнопки оплаты выше:
 *   1) живой выбор «Выбора тарифа» — текущий экран, затем вся воронка
 *      (кросс-экранно, со всеми оговорками resolveSelectedPlanPriceId), а без
 *      живого выбора — тариф селектора по умолчанию (WEB-1714,
 *      resolvePlanSelectorPriceId);
 *   2) своя цена платёжной кнопки ЭТОГО экрана — зеркало ступени
 *      `block.stripePriceId` у кнопки: у блока условий такого поля нет;
 *   3) первый видимый блок с ценой на этом экране.
 * Четвёртая ступень — статичная `paymentTermsPriceId` самого блока условий —
 * живёт у вызывающего (PublicBlockRendererExtended): она не участвует в
 * оплате и применяется, только когда лестница пуста или найденная цена не
 * резолвится в каталоге.
 *
 * Почему цена не резолвится не «проваливает» ступень дальше по лестнице:
 * ступени работают с id, а каталог цен известен только вызывающему. Правило
 * осознанное — id, которого нет в подключённом аккаунте Stripe, публикацию
 * не проходит (pricingGate, reason 'stale'), поэтому на бою такая ступень
 * недостижима; в редакторе же честнее показать статичную цену, чем спрятать
 * блок целиком. Дыры вместо суммы не будет в любом случае:
 * interpolatePaymentTerms скрывает текст, когда цены нет вовсе.
 */
export function resolvePaymentTermsPriceId(
  screenBlocks: EditorBlock[],
  values: Record<string, string> = {},
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): string | undefined {
  return (
    resolvePlanSelectorPriceId(screenBlocks, values, funnelBlocks, hiddenBlockIds) ??
    resolveScreenPayButtonPriceId(screenBlocks, hiddenBlockIds) ??
    resolveFirstPricedBlockId(screenBlocks, hiddenBlockIds)
  );
}

/**
 * WEB-1726 — какая ступень лестницы выше задаёт цену этому экрану ПРЯМО
 * СЕЙЧАС, на языке панели редактора.
 *
 * Зачем. Владелец увидел в блоке «Условия оплаты» цену не из своего поля и
 * решил, что поле сломано. Поле не сломано: порядок ступеней ратифицирован
 * (решение Р2, 24.08, WEB-1302) — текст обязан обещать сумму, которую спишет
 * кнопка. Сломано было ОБЪЯСНЕНИЕ: в панели ничего не говорило, что ручное
 * поле — последний запасной вариант. Эта функция и даёт панели то, что нельзя
 * подсмотреть в разметке: имя сработавшей ступени.
 *
 * ПОЧЕМУ БЕЗ `values`. Резолвер выше отвечает на вопрос «какая цена уйдёт в
 * оплату вот в эту секунду» и потому смотрит на ЖИВОЙ выбор покупателя.
 * Панель отвечает на другой вопрос — «что увидит покупатель», а покупатель
 * увидит выбор тарифа, как только откроет экран: селектор зеркалит свой
 * выбор (в том числе умолчание, на монтировании) в значения формы. Поэтому
 * первая ступень здесь считается по НАЛИЧИЮ селектора, а не по уже сделанному
 * выбору, которого в редакторе не существует.
 *
 * ВТОРОЙ КОПИИ ЛЕСТНИЦЫ ТУТ НЕТ: и порядок, и сами ступени — те же функции,
 * что зовёт `resolvePaymentTermsPriceId`. Сцепку стережёт
 * `paymentTermsPriceSource.web1726.test.tsx`: он краснеет, если ступени
 * переставить или описатель разойдётся с резолвером.
 */
export type PaymentTermsPriceStep =
  /** 1) живой выбор «Выбора тарифа» — этот экран или любой экран воронки. */
  | 'plan-selector'
  /** 2) своя цена платёжной кнопки этого экрана. */
  | 'pay-button'
  /** 3) первый видимый блок с ценой на этом экране. */
  | 'priced-block'
  /** 4) ручное поле «Цена Stripe» самого блока условий — последний вариант. */
  | 'own-field';

export function describePaymentTermsPriceStep(
  screenBlocks: EditorBlock[],
  funnelBlocks?: EditorBlock[],
  hiddenBlockIds?: Set<string>,
): PaymentTermsPriceStep {
  if (findPlanSelector(screenBlocks, funnelBlocks, hiddenBlockIds)) {
    return 'plan-selector';
  }
  if (resolveScreenPayButtonPriceId(screenBlocks, hiddenBlockIds)) {
    return 'pay-button';
  }
  if (resolveFirstPricedBlockId(screenBlocks, hiddenBlockIds)) {
    return 'priced-block';
  }
  return 'own-field';
}
